import { z } from 'zod';
import { Button } from './Button';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Stack } from './Layout';
import { Text, TextLink } from './Text';

export const signInSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
});

export type SignInValues = z.infer<typeof signInSchema>;

export interface SignInFormProps {
  /** From `useZodForm(signInSchema, …)`. */
  form: ZodForm<typeof signInSchema>;
  /** The provider is being reached: the button relabels and locks. */
  submitting: boolean;
  onSubmit: (values: SignInValues) => void;
  /**
   * How this browser last signed in, as a phrase — "with Google as
   * jane@acme.com". Drawn under the email with a "Not you?" that forgets it.
   */
  lastSignIn?: string | null;
  onNotYou?: () => void;
  onForgotPassword: () => void;
  onSignUp: () => void;
}

/**
 * The sign-in screen: an email, Continue, and the links around it. Where the
 * address goes — the identity provider, the analytics event — is the
 * container's business; this draws the form and reports what was typed.
 *
 *   const form = useZodForm(signInSchema, { defaultValues: { email } });
 *   <SignInForm form={form} submitting={processing} onSubmit={signIn}
 *     onForgotPassword={…} onSignUp={…} />
 */
export function SignInForm({
  form,
  submitting,
  onSubmit,
  lastSignIn,
  onNotYou,
  onForgotPassword,
  onSignUp,
}: SignInFormProps) {
  const handleNotYou = () => {
    onNotYou?.();
    form.setFocus('email');
  };

  return (
    <Stack gap={8}>
      <Stack gap={2}>
        <Text variant="h2" tone="default">
          Log in to your account
        </Text>
        <Text variant="body-sm" tone="muted">
          Welcome back to Relay Tow
        </Text>
      </Stack>

      <Form form={form} gap={4} onSubmit={onSubmit}>
        <FormField
          form={form}
          name="email"
          label="Email"
          description={
            lastSignIn ? (
              <>
                Last time you signed in {lastSignIn}.{' '}
                <TextLink as="button" onClick={handleNotYou}>
                  Not you?
                </TextLink>
              </>
            ) : undefined
          }
        >
          {(field) => (
            <Input
              {...field}
              type="email"
              placeholder="jane@acme.com"
              disabled={submitting}
              size="lg"
              autoFocus
            />
          )}
        </FormField>

        <Button size="xl" type="submit" disabled={submitting} fullWidth>
          {submitting ? 'Signing in...' : 'Continue'}
        </Button>

        <Text variant="body-sm" align="center">
          <TextLink as="button" tone="muted" onClick={onForgotPassword}>
            Forgot your password?
          </TextLink>
        </Text>

        <Text variant="caption" tone="muted" align="center">
          By signing in, you agree to our{' '}
          {/* The terms are on the privacy policy's page; relaytow.com/terms
              is not found. */}
          <TextLink
            href="https://relaytow.com/privacy-policy"
            external
            tone="muted"
          >
            Terms of Service
          </TextLink>{' '}
          and{' '}
          <TextLink
            href="https://relaytow.com/privacy-policy"
            external
            tone="muted"
          >
            Privacy Policy
          </TextLink>
        </Text>
      </Form>

      <Text variant="body-sm" tone="muted" align="center">
        Don't have an account yet?{' '}
        <TextLink as="button" onClick={onSignUp}>
          Sign up
        </TextLink>
      </Text>
    </Stack>
  );
}
